import { useParams, useLocation } from "wouter";
import { useFriendVibes } from "@/hooks/use-friends";
import { timeAgo } from "@/lib/time-ago";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Music, Camera, Video } from "lucide-react";
import { VenueDetailDrawer } from "@/components/VenueDetailDrawer";
import { FullscreenVibeViewer, type FeedMediaItem } from "@/components/FullscreenVibeViewer";
import { useState } from "react";
import type { Venue } from "@shared/schema";

export default function FriendVibesPage() {
  const params = useParams<{ friendId: string }>();
  const [, setLocation] = useLocation();
  const friendId = Number(params.friendId);
  const { data, isLoading, error } = useFriendVibes(friendId);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [selectedMedia, setSelectedMedia] = useState<FeedMediaItem | null>(null);

  function openVibe(item: any) {
    try {
      const venueId = item?.venueId;
      const venueName = item?.venueName ?? item?.venue?.name ?? "Unknown venue";
      if (item?.type === "media") {
        const cloudinaryUrl = item.cloudinaryUrl ?? item.mediaUrl ?? item.imageUrl ?? item.videoUrl ?? "";
        if (!cloudinaryUrl) {
          console.warn("[FriendVibesPage] media item missing url", item);
        }
        const media: FeedMediaItem = {
          id: Number(item.id),
          venueId: Number(venueId),
          venueName,
          userId: friendId,
          username: data?.username ?? item?.user?.username ?? item?.username ?? "unknown",
          mediaType: (item.mediaType === "video" ? "video" : "photo"),
          cloudinaryUrl,
          thumbnailUrl: item.thumbnailUrl ?? null,
          durationSeconds: item.durationSeconds ?? null,
          thumbsUp: Number(item.thumbsUp ?? 0),
          thumbsDown: Number(item.thumbsDown ?? 0),
          createdAt: item.date ?? item.createdAt ?? new Date().toISOString(),
        };
        console.log("[FriendVibesPage] open media vibe:", media);
        setSelectedMedia(media);
      } else {
        // report → open venue drawer
        if (!venueId) {
          console.warn("[FriendVibesPage] report missing venueId", item);
          return;
        }
        setSelectedVenue({ id: Number(venueId) } as Venue);
      }
    } catch (err) {
      console.error("[FriendVibesPage] failed to open vibe:", err, item);
    }
  }

  function renderVibeRow(item: any, idx: number) {
    try {
      const venueName = item?.venueName ?? item?.venue?.name ?? "Unknown venue";
      const date = item?.date ?? item?.createdAt;
      if (!item || (!item.id && item.id !== 0) || !item.type) {
        console.warn("[FriendVibesPage] skipping malformed vibe row", item);
        return null;
      }
      const isMedia = item.type === "media";
      return (
        <button
          key={`${item.type}-${item.id}`}
          data-testid={`friend-vibe-${idx}`}
          onClick={() => openVibe(item)}
          className="w-full flex items-center gap-3 p-4 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800/50 transition-all text-left text-white"
        >
          {isMedia ? (
            <div className="w-12 h-12 rounded-xl bg-zinc-800 overflow-hidden shrink-0">
              {item.thumbnailUrl ? (
                <img
                  src={item.thumbnailUrl}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  {item.mediaType === "video" ? <Video className="w-5 h-5 text-zinc-500" /> : <Camera className="w-5 h-5 text-zinc-500" />}
                </div>
              )}
            </div>
          ) : (
            <div className="w-12 h-12 rounded-xl bg-zinc-800 flex items-center justify-center shrink-0">
              <Music className="w-5 h-5 text-zinc-500" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{venueName}</p>
            <p className="text-zinc-500 text-xs truncate">
              {item.type === "report"
                ? `${item.vibe ?? "vibe"} · ${item.capacityLevel ?? 0}% busy`
                : (item.mediaType ?? "media")}
            </p>
          </div>
          <span className="text-zinc-600 text-xs shrink-0">{date ? timeAgo(date) : ""}</span>
        </button>
      );
    } catch (err) {
      console.error("[FriendVibesPage] failed to render vibe row:", err, item);
      return null;
    }
  }

  return (
    <div className="min-h-screen bg-background text-white">
      <div className="px-4 pt-[max(3rem,calc(env(safe-area-inset-top)+1rem))] pb-4 flex items-center gap-3">
        <button
          data-testid="button-back-from-vibes"
          onClick={() => {
            // Return-to flags are read by Home.tsx on mount and acted upon there.
            setLocation("/");
          }}
          className="w-10 h-10 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        {data?.profilePhotoUrl ? (
          <img
            src={data.profilePhotoUrl}
            alt={data.username}
            data-testid="friend-vibes-avatar"
            className="w-10 h-10 rounded-full object-cover shrink-0 border-2 border-zinc-700"
          />
        ) : data?.username ? (
          <div
            data-testid="friend-vibes-avatar"
            className="w-10 h-10 rounded-full bg-zinc-800 border-2 border-zinc-700 flex items-center justify-center shrink-0"
          >
            <span className="text-sm font-bold text-white">{data.username[0].toUpperCase()}</span>
          </div>
        ) : null}
        <h1 className="text-xl font-display font-bold">
          {data?.username ? `@${data.username}'s Vibes` : "Friend's Vibes"}
        </h1>
      </div>

      <div className="px-4 pb-8 space-y-2">
        {isLoading ? (
          Array(5).fill(0).map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-4 rounded-2xl bg-zinc-900/50 border border-zinc-800/50">
              <Skeleton className="w-12 h-12 rounded-xl bg-zinc-800" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-4 w-1/3 bg-zinc-800" />
                <Skeleton className="h-3 w-1/2 bg-zinc-800" />
              </div>
            </div>
          ))
        ) : error ? (
          <div className="text-center py-16">
            <Music className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm" data-testid="text-friend-vibes-error">
              {String((error as Error)?.message ?? error).startsWith("403")
                ? "You're not friends yet — accept the friend request first."
                : "Couldn't load vibes. Try again in a moment."}
            </p>
          </div>
        ) : (data?.vibes || []).length === 0 ? (
          <div className="text-center py-16">
            <Music className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">Nothing reported yet</p>
          </div>
        ) : (
          (data?.vibes || []).map((item: any, idx: number) => renderVibeRow(item, idx))
        )}
      </div>

      <VenueDetailDrawer
        venue={selectedVenue}
        isOpen={!!selectedVenue}
        onClose={() => setSelectedVenue(null)}
      />

      <FullscreenVibeViewer
        media={selectedMedia}
        onClose={() => setSelectedMedia(null)}
        onViewVenue={(venueId) => {
          setSelectedMedia(null);
          setSelectedVenue({ id: Number(venueId) } as Venue);
        }}
      />
    </div>
  );
}
